import DeleteConfirmationModal from '@app/components/DeleteConfirmationModal'
import FormGroupRenderer, {
  ILabelColumnSize,
} from '@app/components/FormGroupRenderer'
import { NavTabItem, NavTabs } from '@app/components/NavTabs'
import PillListRenderer from '@app/components/PillListRenderer'
import { AssignmentSourceType } from '@app/models/enums'
import MaintenancePage from '@app/routes/Maintenance/MaintenancePage'
import { ICurrentUserState } from '@app/store/modules/account'
import { IGlobalSocialNetworksState } from '@app/store/modules/global'
import { bindFunctions } from '@app/utils/class'
import { formatCurrency, formatDate, humanize } from '@app/utils/formatting'
import { IconName, IconPrefix } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as React from 'react'
import DocumentTitle from 'react-document-title'
import { Link, Route, RouteComponentProps, Switch } from 'react-router-dom'
import {
  Card,
  CardBody,
  CardHeader,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from 'reactstrap'
import {
  actionCreators,
  IAssignmentDetailState,
} from '../modules/assignmentDetail'
import AssignmentActivity from './AssignmentActivity'
import AssignmentDetails from './AssignmentDetails'
import AssignmentFiles from './AssignmentFiles'

interface IComponentRouterProps {
  id: string
}

type IComponentProps = IAssignmentDetailState &
  ICurrentUserState &
  IGlobalSocialNetworksState &
  RouteComponentProps<IComponentRouterProps> &
  typeof actionCreators

export default class AssignmentViewPage extends React.PureComponent<IComponentProps> {
  private assignmentId: string
  private labelColSizes: ILabelColumnSize

  constructor(props: IComponentProps) {
    super(props)

    bindFunctions(this)

    this.labelColSizes = {
      md: 4,
      xl: 3,
    }
  }

  public componentDidMount() {
    const { id } = this.props.match.params
    this.assignmentId = id
    this.props.loadAssignment(id)
  }

  public componentDidUpdate(
    prevProps: IComponentProps,
    prevState: any,
    snapshot: any,
  ) {
    const { id } = this.props.match.params
    if (prevProps.item && prevProps.item.id !== id) {
      this.assignmentId = id
      this.props.loadAssignment(id)
    }
  }

  public render() {
    if (!this.props.item || !this.props.user) {
      return <div />
    }

    return (
      <DocumentTitle title={this.props.item.title}>
        <Container className='mt-3'>
          <Card className='d-flex flex-column mt-2'>
            <CardHeader className='bg-secondary text-white'>
              {this._renderCardHeader()}
            </CardHeader>
            <CardBody>{this._renderCardBody()}</CardBody>
          </Card>
          {this._renderTabs()}
          <Switch>
            <Route
              exact
              path={this.props.match.path}
              component={AssignmentActivity}
            />
            <Route
              exact
              path={`${this.props.match.path}/details`}
              render={() => <AssignmentDetails {...this.props} />}
            />
            <Route
              exact
              path={`${this.props.match.path}/files`}
              render={() => <AssignmentFiles {...this.props} />}
            />
            <Route component={MaintenancePage} />
          </Switch>
          <DeleteConfirmationModal
            isOpen={this.props.showConfirmDelete}
            itemName={this.props.item.title}
            onConfirm={() => this.props.deleteAssignment(this.props.item.id)}
            onToggleModal={() => this.props.toggleConfirmDelete(false)}
          />
        </Container>
      </DocumentTitle>
    )
  }

  private _renderTabs() {
    return (
      <div className='mb-3 mt-3'>
        <NavTabs>
          <NavTabItem
            exact
            iconName='chart-line'
            label='Activity'
            to={this.props.match.url}
          />
          <NavTabItem
            iconName='list'
            label='Details'
            to={`${this.props.match.url}/details`}
          />
          <NavTabItem
            badgeCount={this._calculateAttachmentBadgeCount()}
            iconName='file'
            label='Files'
            to={`${this.props.match.url}/files`}
          />
        </NavTabs>
      </div>
    )
  }

  private _calculateAttachmentBadgeCount() {
    let total = 0

    this.props.item.relatedAttachments.forEach((item: any) => {
      total += item.attachments.size + item.photos.size
    })

    return total + this.props.item.attachments.size
  }

  private _renderCardBody() {
    return (
      <div className='d-flex flex-column flex-wrap'>
        <div>
          <strong>
            {this.props.item.title}{' '}
            {this.props.item.deleted && (
              <span className='text-red'> (DELETED)</span>
            )}
          </strong>
        </div>

        <div className='d-flex flex-row flex-wrap justify-content-between mt-2'>
          <div className='d-flex flex-column flex-grow-1'>
            <FormGroupRenderer style={{ marginBottom: 0 }} labelText='Company'>
              {this.props.item.company
                ? this.props.item.company.name
                : 'No Company'}
            </FormGroupRenderer>

            <FormGroupRenderer
              style={{ marginBottom: 0 }}
              labelText='Connections'
            >
              <PillListRenderer
                list={this.props.item.contacts}
                baseNavigateUrl='/connection'
                idValue='contactId'
              />
            </FormGroupRenderer>

            <FormGroupRenderer
              style={{ marginBottom: 0 }}
              labelText='Properties'
            >
              <PillListRenderer
                list={this.props.item.properties}
                baseNavigateUrl='/properties/property'
                idValue='propertyId'
              />
            </FormGroupRenderer>
          </div>

          <div className='d-flex flex-column flex-grow-1'>
            {this.props.item.source &&
              this.props.item.source.type ===
                AssignmentSourceType.BrokeragePipeline && (
                <FormGroupRenderer
                  style={{ marginBottom: 0 }}
                  labelColSizes={this.labelColSizes}
                  labelText='Critical Date for Pipeline'
                >
                  <Link
                    to={`/brokerage/pipeline/${this.props.item.source.sourceId}`}
                  >
                    {this.props.item.source.name}
                  </Link>
                </FormGroupRenderer>
              )}
            <FormGroupRenderer
              style={{ marginBottom: 0 }}
              labelColSizes={this.labelColSizes}
              labelText='Due Date'
            >
              {this.props.item.dueDate
                ? formatDate(this.props.item.dueDate, 'MM/DD/YYYY')
                : 'No Due Date'}
            </FormGroupRenderer>
            <FormGroupRenderer
              style={{ marginBottom: 0 }}
              labelColSizes={this.labelColSizes}
              labelText='Label'
            >
              {humanize(this.props.item.label)}
            </FormGroupRenderer>
            <FormGroupRenderer
              style={{ marginBottom: 0 }}
              labelColSizes={this.labelColSizes}
              labelText='Size'
            >
              {this.props.item.lotSize.toString()}
            </FormGroupRenderer>
            <FormGroupRenderer
              style={{ marginBottom: 0 }}
              labelColSizes={this.labelColSizes}
              labelText='Dollar Opportunity'
            >
              {formatCurrency(this.props.item.dollarOpportunity)}
            </FormGroupRenderer>
          </div>
        </div>
      </div>
    )
  }

  private _renderCardHeader() {
    if (!this.props.user) {
      return null
    }

    const { id } = this.props.user

    const assignmentHeaderIcon: [IconPrefix, IconName] = ['fal', 'check-square']

    return (
      <div className='d-flex flex-row'>
        <span>
          <FontAwesomeIcon
            className='primary-color'
            icon={assignmentHeaderIcon}
            fixedWidth
          />{' '}
          Assignment
        </span>
        <div className='ml-auto'>
          <UncontrolledDropdown style={{ display: 'inline' }}>
            <DropdownToggle tag='a' className='px-1 text-center'>
              <FontAwesomeIcon icon={['fal', 'chevron-down']} fixedWidth />
            </DropdownToggle>
            {this._renderDropdown()}
          </UncontrolledDropdown>
        </div>
      </div>
    )
  }

  private _renderDropdown() {
    const menuItems = []
    const item = this.props.item

    if (this._canEdit()) {
      menuItems.push(
        <Link key='edit' to={`/assignment/${this.assignmentId}/edit`}>
          <DropdownItem>Edit</DropdownItem>
        </Link>,
      )
    }

    if (this._canDelete()) {
      menuItems.push(
        <DropdownItem
          disabled={this.props.isDeleting}
          key='delete'
          onClick={() => this.props.toggleConfirmDelete(true)}
        >
          Delete
        </DropdownItem>,
      )
    }

    return <DropdownMenu>{menuItems}</DropdownMenu>
  }

  private _canDelete() {
    return true
  }

  private _canEdit() {
    return true
  }
}
